/**
 * The cart, as pure functions.
 *
 * Every operation takes a cart and returns a new one. Nothing here reaches a
 * store, a database or the network — which is what lets the page hold a cart in
 * Zustand, the server re-price the same cart from its own catalogue, and a test
 * exercise both without either.
 *
 * The Electron `sales` router held the cart *server-side*: `addToCart`,
 * `updateQuantity`, `removeItem` and `clearCart` were each a round trip that
 * wrote a row. Free over IPC on the same machine; a customer waiting on the
 * network to add vanilla over HTTPS.
 */
import type { Menu, OrderType, Product, Sku, Temperature } from './catalogue'
import { isOrderable } from './catalogue'
import type { LineSelection, PricedLine } from './line'
import { lineKey, priceLine, remainingStock } from './line'
import type { Money } from './money'
import { ZERO, sum } from './money'

export interface Cart {
  readonly orderType: OrderType | null
  readonly lines: readonly LineSelection[]
}

export const emptyCart: Cart = { orderType: null, lines: [] }

/** A product lookup. The page has a `Map`; the server has a query. */
export type Catalogue = { get(id: Sku): Product | undefined }

export function catalogueOf(menu: Menu | readonly Product[]): Catalogue {
  const products = Array.isArray(menu) ? (menu as readonly Product[]) : (menu as Menu).products
  const byId = new Map(products.map((p) => [p.id, p]))
  return { get: (id) => byId.get(id) }
}

/** What the customer is about to add, before it has an id or a quantity. */
export interface AddRequest {
  readonly productId: Sku
  readonly quantity?: number
  readonly temperature?: Temperature
  readonly changeCode?: Sku
  readonly addCodes?: readonly Sku[]
  readonly orderType?: OrderType
  readonly note?: string
}

export type AddRefusal =
  | { readonly reason: 'unknown-product'; readonly productId: Sku }
  | { readonly reason: 'not-orderable'; readonly productId: Sku }
  | { readonly reason: 'out-of-stock'; readonly productId: Sku; readonly available: number }

export type AddResult = { ok: true; cart: Cart; lineId: string } | { ok: false; refusal: AddRefusal }

/** How many of a product are already in the cart, across every line. */
export function quantityOf(cart: Cart, productId: Sku): number {
  return cart.lines.reduce((n, line) => (line.productId === productId ? n + line.quantity : n), 0)
}

/**
 * Add to the cart, merging into an identical line.
 *
 * "Identical" is `lineKey` — the product *and* everything chosen on it. The old
 * store merged on `productId` alone, so a latte with an extra shot joined a
 * latte without one and took its price.
 *
 * `newId` is passed in rather than generated, because a pure function that
 * calls `crypto.randomUUID()` is not one, and a cart that replays differently
 * on the server than it did on the page is a cart nobody can reconcile.
 */
export function addLine(cart: Cart, catalogue: Catalogue, request: AddRequest, newId: string): AddResult {
  const product = catalogue.get(request.productId)
  if (product === undefined) return { ok: false, refusal: { reason: 'unknown-product', productId: request.productId } }

  const orderType = request.orderType ?? cart.orderType
  if (!isOrderable(product, orderType)) {
    return { ok: false, refusal: { reason: 'not-orderable', productId: product.id } }
  }

  const wanted = request.quantity ?? 1
  const available = remainingStock(product, quantityOf(cart, product.id))
  if (wanted > available) {
    return { ok: false, refusal: { reason: 'out-of-stock', productId: product.id, available } }
  }

  const shape = {
    productId: product.id,
    temperature: request.temperature,
    changeCode: request.changeCode,
    addCodes: request.addCodes ?? [],
    orderType: request.orderType,
    note: request.note
  } satisfies Omit<LineSelection, 'id' | 'quantity'>

  const key = lineKey(shape)
  const existing = cart.lines.find((line) => lineKey(line) === key)

  if (existing !== undefined) {
    return {
      ok: true,
      lineId: existing.id,
      cart: {
        ...cart,
        lines: cart.lines.map((line) =>
          line.id === existing.id ? { ...line, quantity: line.quantity + wanted } : line
        )
      }
    }
  }

  const line: LineSelection = { id: newId, quantity: wanted, ...shape }
  return { ok: true, lineId: newId, cart: { ...cart, lines: [...cart.lines, line] } }
}

/** Set a line's quantity. Zero removes it, which is what a `−` button at 1 means. */
export function setQuantity(cart: Cart, catalogue: Catalogue, lineId: string, quantity: number): AddResult {
  if (quantity <= 0) return { ok: true, cart: removeLine(cart, lineId), lineId }

  const line = cart.lines.find((l) => l.id === lineId)
  if (line === undefined) return { ok: true, cart, lineId }

  const product = catalogue.get(line.productId)
  if (product === undefined) return { ok: false, refusal: { reason: 'unknown-product', productId: line.productId } }

  const others = quantityOf(cart, line.productId) - line.quantity
  const available = remainingStock(product, others)
  if (quantity > available) {
    return { ok: false, refusal: { reason: 'out-of-stock', productId: product.id, available } }
  }

  return {
    ok: true,
    lineId,
    cart: { ...cart, lines: cart.lines.map((l) => (l.id === lineId ? { ...l, quantity } : l)) }
  }
}

export function removeLine(cart: Cart, lineId: string): Cart {
  return { ...cart, lines: cart.lines.filter((line) => line.id !== lineId) }
}

export function setLineOrderType(
  cart: Cart,
  catalogue: Catalogue,
  lineId: string,
  orderType: OrderType | undefined
): Cart {
  const line = cart.lines.find((l) => l.id === lineId)
  if (line === undefined) return cart
  const product = catalogue.get(line.productId)
  const next = orderType ?? cart.orderType
  if (product !== undefined && !isOrderable(product, next)) return cart
  return { ...cart, lines: cart.lines.map((l) => (l.id === lineId ? { ...l, orderType } : l)) }
}

/**
 * Set the order's type, and drop what it makes unorderable.
 *
 * Switching to take-away with an `onlyTakeIn` item in the cart has to do
 * *something*, and silently keeping it means a checkout 1C refuses at the till
 * with the customer's card already in the terminal. The removed lines come back
 * so the screen can say which.
 */
export function setOrderType(
  cart: Cart,
  catalogue: Catalogue,
  orderType: OrderType
): { cart: Cart; removed: readonly LineSelection[] } {
  const removed: LineSelection[] = []
  const lines = cart.lines.filter((line) => {
    const product = catalogue.get(line.productId)
    const effective = line.orderType ?? orderType
    if (product !== undefined && !isOrderable(product, effective)) {
      removed.push(line)
      return false
    }
    return true
  })
  return { cart: { orderType, lines }, removed }
}

export const clear = (cart: Cart): Cart => ({ orderType: cart.orderType, lines: [] })

export const isEmpty = (cart: Cart): boolean => cart.lines.length === 0

export const itemCount = (cart: Cart): number => cart.lines.reduce((n, line) => n + line.quantity, 0)

/**
 * Price the whole cart.
 *
 * A line whose product has left the catalogue is dropped and reported rather
 * than throwing, because a mid-order catalogue refresh should not take the
 * screen down — the customer is told the item is no longer available.
 */
export function priceCart(
  cart: Cart,
  catalogue: Catalogue
): { lines: readonly PricedLine[]; total: Money; savings: Money; dropped: readonly LineSelection[] } {
  const lines: PricedLine[] = []
  const dropped: LineSelection[] = []

  for (const selection of cart.lines) {
    const product = catalogue.get(selection.productId)
    if (product === undefined) {
      dropped.push(selection)
      continue
    }
    try {
      lines.push(priceLine(product, selection))
    } catch {
      dropped.push(selection)
    }
  }

  return {
    lines,
    dropped,
    total: lines.length === 0 ? ZERO : sum(lines.map((l) => l.total)),
    savings: lines.length === 0 ? ZERO : sum(lines.map((l) => l.savings))
  }
}

/** The cart total alone, for a badge that does not need the lines. */
export function cartTotal(cart: Cart, catalogue: Catalogue): Money {
  return priceCart(cart, catalogue).total
}
